/**
 * The human-handoff queue for the Mastra CX agent ("Kate").
 *
 * Kate answers WhatsApp and Messenger on her own, but when a conversation needs
 * a person she flags it and goes quiet. This duck talks to api-backend's
 * `chatbot.handoff:list` / `chatbot.handoff:resolve` — and resolving a case is
 * what makes her start answering that customer again, so these are not
 * read-only admin screens.
 */
import constantCreator from 'utils/constantCreator';
import api from 'utils/api';

const define = constantCreator('cxHandoff');

const GET_HANDOFFS = define('GET_HANDOFFS', true);
const RESOLVE_HANDOFF = define('RESOLVE_HANDOFF', true);
const SET_FILTERS = define('SET_FILTERS');

// Kate is silent in the first two; `human_notified` only means the team was
// pinged. All three are "nobody has closed this yet", which is the queue.
export const OPEN_STATUSES = ['human_required', 'human_active', 'human_notified'];

const initialState = {
  isLoading: false,
  processing: false,
  handoffs: [],
  total: 0,
  filters: {
    status: OPEN_STATUSES,
    platform: undefined,
    sort: 'oldest',
    page: 1,
    limit: 20
  },
  error: null
};

export default (state = initialState, action) => {
  switch (action.type) {
    case GET_HANDOFFS.PENDING:
      return { ...state, isLoading: true, error: null };
    case GET_HANDOFFS.RESOLVED:
      return {
        ...state,
        isLoading: false,
        handoffs: action.data.handoffs || [],
        total: action.data.total || 0
      };
    case GET_HANDOFFS.REJECTED:
      return { ...state, isLoading: false, error: action.error };

    case RESOLVE_HANDOFF.PENDING:
      return { ...state, processing: true, error: null };
    case RESOLVE_HANDOFF.RESOLVED: {
      const updated = action.data.handoff;
      if (!updated) return { ...state, processing: false };
      return {
        ...state,
        processing: false,
        // Patch the row in place rather than refetching: the list is sorted by
        // how long someone has waited, and a refetch would reshuffle the table
        // under the cursor of whoever just clicked.
        handoffs: state.handoffs.map(h => (h._id === updated._id ? updated : h))
      };
    }
    case RESOLVE_HANDOFF.REJECTED:
      return { ...state, processing: false, error: action.error };

    case SET_FILTERS:
      return { ...state, filters: { ...state.filters, ...action.filters } };

    default:
      return state;
  }
};

/** Filters as the API wants them: `status` is comma-separated, blanks omitted. */
const toQuery = (filters) => {
  const params = {
    sort: filters.sort || 'oldest',
    page: filters.page || 1,
    limit: filters.limit || 20
  };
  if (filters.status && filters.status.length > 0) {
    params.status = filters.status.join(',');
  }
  if (filters.platform) params.platform = filters.platform;
  return new URLSearchParams(params).toString();
};

export const actions = {
  getHandoffs: (filters = initialState.filters) => ({
    type: GET_HANDOFFS,
    promise: api.get(`/mastra/handoffs?${toQuery(filters)}`)
  }),

  /**
   * `status` is what the agent reads on the customer's next message:
   * `human_active` keeps her quiet while an admin replies, `resolved` hands the
   * conversation back to her, `human_required` puts it back in the queue.
   */
  resolveHandoff: (handoffId, body) => ({
    type: RESOLVE_HANDOFF,
    promise: api.post(`/mastra/handoffs/${handoffId}`, body),
    success: 'Handoff updated'
  }),

  setFilters: (filters) => ({ type: SET_FILTERS, filters }),

  // Changing a filter always resets to page 1 — staying on page 4 of a
  // narrower result set shows an empty table and looks like a broken screen.
  applyFiltersAndFetch: (changes) => (dispatch, getState) => {
    const resetPage = Object.keys(changes).indexOf('page') === -1;
    const filters = {
      ...getState().cxHandoff.filters,
      ...changes,
      ...(resetPage ? { page: 1 } : {})
    };
    dispatch(actions.setFilters(filters));
    return dispatch(actions.getHandoffs(filters));
  }
};



// WEBPACK FOOTER //
// ./src/ducks/cx-handoff.js